import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, isAuthenticated } from '../api/client';
import { Zap, Lock, User, ArrowRight, Loader2 } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await auth.login(username, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-[hsl(222,47%,6%)]">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid opacity-30" />

        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-500/15 rounded-full blur-[80px] animate-float [animation-delay:2s]" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-violet-500/10 rounded-full blur-[60px] animate-float [animation-delay:4s]" />

        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-[hsl(222,47%,4%)]" />
      </div>

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md px-4 animate-fade-in-up">
        <div className="modal-content rounded-2xl p-8 relative overflow-hidden">
          {/* Decorative corner glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-500/10 blur-3xl" />

          {/* Logo/Header */}
          <div className="relative text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 mb-4 shadow-glow-lg">
              <Zap className="w-8 h-8 text-[hsl(222,47%,6%)]" />
            </div>
            <h1 className="text-3xl font-bold text-gradient tracking-tight">
              Neural Command
            </h1>
            <p className="mt-2 text-muted-foreground">
              AI Proxy Administration
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="alert-error rounded-lg px-4 py-3 mb-6 text-sm animate-scale-in">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-muted-foreground">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-neural w-full pl-11 pr-4 py-3 rounded-xl text-sm"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-muted-foreground">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-neural w-full pl-11 pr-4 py-3 rounded-xl text-sm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-glow w-full py-3 px-4 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              <span className="flex items-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Access Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-border/50">
            <p className="text-center text-xs text-muted-foreground">
              Secure connection established
              <span className="inline-flex items-center ml-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-live" />
              </span>
            </p>
          </div>
        </div>

        {/* Version tag */}
        <p className="text-center text-xs text-muted-foreground/50 mt-6 font-mono">
          v1.0.0 • Neural Command Interface
        </p>
      </div>
    </div>
  );
}
